import { Dialog } from "@headlessui/react";
import { ChevronRightIcon, PlusIcon } from "@heroicons/react/solid";
import { useAuth } from "context/auth-context";
import { usePrinter } from "context/printer-context";
import {
  useCashDrawer,
  useCloseCashDrawer,
  useOpenCashDrawer,
  useOpenCashDrawers,
} from "hooks/cash-drawer";
import { AlertError, Button, Form, Loader, Slider } from "lib";
import { DateTime } from "luxon";
import Router from "next/router";
import { useTranslation } from "next-i18next";
import { Fragment, type ReactNode, useState } from "react";
import { DATE_MEDIUM } from "utils/dates";
import { formatMoney } from "utils/format-money";
import { ZPLBuilder } from "utils/zpl-builder";

function CashDrawerTitle({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const { data = [] } = useOpenCashDrawers();
  const qty = data.length;

  return (
    <div>
      <div className="flex items-center justify-between border-b px-4 py-4 sm:px-6">
        {qty === 0 ? (
          <p className="text-sm text-gray-600">{t("cashDrawer.noOpen")}</p>
        ) : (
          <div className="flex items-center space-x-2">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-sm text-gray-600">
              {t("cashDrawer.openDetail", { count: qty })}
            </span>
          </div>
        )}
        <button
          className="rounded border px-2 py-1 text-sm font-medium hover:bg-gray-100"
          onClick={() => {
            onClick();
            void Router.push("/cashDrawer");
          }}
        >
          {t("cashDrawer.detail")}
        </button>
      </div>
      <div className="border-b px-4 py-6 sm:px-6">
        <Dialog.Title className="text-xl font-bold text-gray-900">{children}</Dialog.Title>
      </div>
    </div>
  );
}

function OpenNewCashDrawer({ onClose, onBack }: { onClose: () => void; onBack: () => void }) {
  const { t } = useTranslation();
  const { mutateAsync, methods, error } = useOpenCashDrawer({
    onSuccess: () => onClose(),
  });

  return (
    <>
      <CashDrawerTitle onClick={() => onClose()}>{t("cashDrawer.open")}</CashDrawerTitle>
      <div className="relative mt-6 flex-1">
        <Form
          className="flex h-full flex-col"
          methods={methods}
          onSubmit={async (_, data) => {
            await mutateAsync(data).catch(() => null);
          }}
        >
          <div className="flex-1 space-y-6 overflow-y-auto px-4 sm:px-6">
            <Form.Number
              required
              label={t("cashDrawer.fields.initialAmount")}
              min={0}
              name="initialAmount"
              placeholder="0.000,00"
              prepend="$"
              step="0.01"
            />
            <Form.Text help={t("generic.optional")} label={t("generic.description")} name="name" />
            <AlertError>{error}</AlertError>
          </div>
          <nav className="flex shrink-0 space-x-6 border-t px-4 py-4 sm:px-6">
            <Button className="sm:px-12" kind="light" onClick={() => onBack()}>
              {t("generic.cancel")}
            </Button>
            <Form.SubmitButton block kind="cash">
              {t("cashDrawer.open")}
            </Form.SubmitButton>
          </nav>
        </Form>
      </div>
    </>
  );
}

function CloseCashDrawer({
  name,
  onCancel,
  onSuccess,
  sessionId,
}: {
  name?: string | null;
  onCancel: () => void;
  onSuccess: () => void;
  sessionId: string;
}) {
  const { t } = useTranslation();
  const { methods, mutateAsync, error } = useCloseCashDrawer({ onSuccess });

  return (
    <>
      <CashDrawerTitle onClick={() => onSuccess()}>
        {t("cashDrawer.close", { name: name ?? "" })}
      </CashDrawerTitle>
      <div className="relative mt-6 flex-1">
        <Form
          className="flex h-full flex-col"
          methods={methods}
          onSubmit={async (_, data) => {
            await mutateAsync({ ...data, sessionId }).catch(() => null);
          }}
        >
          <div className="flex-1 space-y-6 overflow-y-auto px-4 sm:px-6">
            <Form.Number
              required
              label={t("cashDrawer.fields.closeAmount")}
              name="closedDrawerAmount"
              placeholder="0.000,00"
              prepend="$"
              step="0.01"
            />
            <AlertError>{error}</AlertError>
          </div>
          <nav className="flex shrink-0 space-x-6 border-t px-4 py-4 sm:px-6">
            <Button className="sm:px-12" kind="light" onClick={() => onCancel()}>
              {t("generic.cancel")}
            </Button>
            <Form.SubmitButton block>{t("cashDrawer.close", { name: "" })}</Form.SubmitButton>
          </nav>
        </Form>
      </div>
    </>
  );
}

function CashDrawerSection({
  data,
  title,
}: {
  data: Array<[string, string, string | undefined]>;
  title: string;
}) {
  return (
    <div className="space-y-4">
      <h3 className="border-b border-t py-3 font-bold">{title}</h3>
      {data.map(([key, label, value]) => (
        <dl key={key} className="flex justify-between">
          <dt className="items-center text-sm font-medium text-gray-500">{label}</dt>
          <dd>{value}</dd>
        </dl>
      ))}
    </div>
  );
}

function CashDrawerDetail({
  sessionId,
  onClose,
  onCancel,
  select = false,
}: {
  sessionId: string;
  onClose: () => void;
  onCancel?: () => void;
  select?: boolean;
}) {
  const { t } = useTranslation();
  const { print } = usePrinter();
  const { sessionId: userSession, setSession, user } = useAuth();
  const { data } = useCashDrawer(sessionId, {
    onSuccess: (cashDrawer) => {
      if (cashDrawer.status !== "OPEN" && userSession === cashDrawer.sessionId) setSession(null);
    },
    onError: () => setSession(null),
  });
  const [close, setClose] = useState(false);

  const printCashDrawer = async () => {
    if (data === undefined || user === null) {
      return;
    }

    const z = new ZPLBuilder({
      width: 400,
      length: 1119,
      orientation: "I",
      startY: 1055,
      defaultFont: { family: "0", h: 20, w: 19 },
      lineGap: 28,
      labelX: 388,
      valueX: 230,
    });

    z.title(t("cashDrawer.print.title"), { x: 300, font: { family: "0", h: 28, w: 27 }, gap: 32 });

    // Cabecera
    z.line(t("cashDrawer.print.user"), `${user.firstName}, ${user.lastName}`);
    z.line(t("cashDrawer.print.email"), user.email);
    z.line(t("cashDrawer.print.cashDrawer"), data.name);
    if (data.number) {
      z.line(t("cashDrawer.print.cashDrawerNumber"), data.number);
    }
    z.line(t("cashDrawer.print.openingDate"), data.getStartDate());
    z.line(t("cashDrawer.print.closingDate"), data.getEndDate() ?? "");
    z.line(t("cashDrawer.print.printDate"), DateTime.now().toLocaleString(DATE_MEDIUM));
    z.line(t("cashDrawer.print.totalAmount"), data.getTotal());

    z.hr();

    // Efectivo
    z.section(t("cashDrawer.print.cashTitle"), { x: 300, font: { family: "0", h: 20, w: 19 } });
    z.line(t("cashDrawer.print.initialCash"), data.getInitialAmount());
    z.line(t("cashDrawer.print.salesInCash"), data.getCash());
    z.line(t("cashDrawer.print.expectedAmount"), data.getCashExpected());
    z.line(t("cashDrawer.print.closeAmount"), data.getClosedDrawerAmount());
    z.line(t("cashDrawer.print.diff"), data.getDifferenceText());

    z.hr();

    // Tarjetas
    z.section(t("cashDrawer.print.cardtitle"), { x: 300, font: { family: "0", h: 20, w: 19 } });
    z.line(t("cashDrawer.print.creditSales"), data.getCredit());
    z.line(t("cashDrawer.print.debitSale"), data.getDebit());

    z.hr();
    z.section(t("cashDrawer.print.operationsTitle"), {
      x: 300,
      font: { family: "0", h: 20, w: 19 },
    });

    const grid = { xs: [388, 250, 200, 150], gap: 26, useFB: false };

    z.columns(
      [
        { text: t("cashDrawer.print.paymentType") },
        { text: "Ops" },
        { text: "Tks" },
        { text: t("cashDrawer.print.amount") },
      ],
      grid,
    );

    data.getItems().forEach((item) => {
      z.columns(
        [
          { text: t(`payment.paymentTypes.${item.paymentType}`) },
          { text: item.purchases },
          { text: item.tickets },
          { text: formatMoney(item.amount) },
        ],
        grid,
      );
    });

    z.columns(
      [
        { text: "Total" },
        { text: data.purchases },
        { text: data.tickets },
        { text: data.getTotal() },
      ],
      grid,
    );

    const zpl = z.build();

    await print(zpl);
  };

  if (close)
    return (
      <CloseCashDrawer
        name={data?.name}
        sessionId={sessionId}
        onCancel={() => setClose(false)}
        onSuccess={() => onClose()}
      />
    );

  return (
    <>
      <CashDrawerTitle onClick={() => onClose()}>
        {t("cashDrawer.cashDrawer")} {data?.name}
      </CashDrawerTitle>
      <div className="relative mt-6 flex h-full flex-1 flex-col">
        <div className="mb-4 flex-1 space-y-6 overflow-y-auto px-4 sm:px-6">
          <dl>
            {(
              [
                ["start", data?.getStartDate() ?? "-"],
                ["end", data?.getEndDate() ?? "-"],
                ["expected", data?.getTotal()],
                ["current", data?.getClosedDrawerAmount()],
              ] as const
            ).map(([key, value]) => (
              <Fragment key={key}>
                <dt className="text-xs font-semibold uppercase text-gray-500">
                  {t(`cashDrawer.details.${key}`)}
                </dt>
                <dd className="mb-6">{value}</dd>
              </Fragment>
            ))}
          </dl>
          <CashDrawerSection
            data={[
              ["initialCash", t("cashDrawer.details.initialCash"), data?.getInitialAmount()],
              ["cashSales", t("cashDrawer.details.cashSales"), data?.getCash()],
              ["expected", t("cashDrawer.details.expected"), data?.getCashExpected()],
              ["current", t("cashDrawer.details.current"), "-"],
            ]}
            title={t("cashDrawer.cashReport")}
          />
          <CashDrawerSection
            data={[
              ["debitSales", t("cashDrawer.details.debitSales"), data?.getDebit()],
              ["creditSales", t("cashDrawer.details.creditSales"), data?.getCredit()],
            ]}
            title={t("cashDrawer.cardReport")}
          />
          <CashDrawerSection
            data={[["otherSales", t("cashDrawer.details.otherSales"), data?.getOther()]]}
            title={t("cashDrawer.otherReport")}
          />
        </div>

        {select ? (
          <nav className="flex shrink-0 space-x-6 border-t px-4 py-4 sm:px-6">
            <Button className="sm:px-12" kind="light" onClick={() => onCancel?.()}>
              {t("generic.cancel")}
            </Button>
            <Button
              block
              kind="cash"
              onClick={() => {
                setSession(sessionId);
                onClose();
              }}
            >
              {t("cashDrawer.select")}
            </Button>
          </nav>
        ) : data?.status === "OPEN" ? (
          <nav className="border-t px-4 py-4 sm:px-6">
            <Button block onClick={() => setClose(true)}>
              {t("cashDrawer.close", { name: "" })}
            </Button>
          </nav>
        ) : (
          <Button
            block
            onClick={() => {
              void printCashDrawer();
            }}
          >
            Imprimir
          </Button>
        )}
      </div>
    </>
  );
}

const NEW_CASH_DRAWER = "NEW_CASH_DRAWER";

function OpenCashDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [newCashDrawer, setNewCashDrawer] = useState<string | null>(null);
  const { isLoading, isSuccess, data = [], error } = useOpenCashDrawers();

  if (isLoading) return <Loader className="my-auto" />;

  if (newCashDrawer === NEW_CASH_DRAWER || (isSuccess && data.length === 0))
    return <OpenNewCashDrawer onBack={() => setNewCashDrawer(null)} onClose={onClose} />;

  if (newCashDrawer !== null)
    return (
      <CashDrawerDetail
        select
        sessionId={newCashDrawer}
        onCancel={() => setNewCashDrawer(null)}
        onClose={onClose}
      />
    );

  return (
    <>
      <CashDrawerTitle onClick={() => onClose()}>
        {t("cashDrawer.selectCashDrawer")}
      </CashDrawerTitle>
      <div className="relative mt-6 flex h-full flex-1 flex-col">
        <ul className="flex-1 space-y-3 overflow-y-auto px-4 sm:px-6">
          {data.map((c) => (
            <li key={c.id}>
              <button
                className="flex w-full items-center rounded border px-5 py-5 hover:bg-gray-100"
                onClick={() => setNewCashDrawer(c.sessionId)}
              >
                <div className="flex flex-1 items-baseline">
                  <div className="h-3 w-3 rounded-full bg-green-500 shadow" />
                  <div className="ml-3 text-left">
                    <h4 className="font-bold">{c.name}</h4>
                    <p className="text-sm text-gray-500">{c.getStartDate()}</p>
                  </div>
                </div>
                <ChevronRightIcon aria-hidden="true" className="h-5 w-5 text-gray-600" />
              </button>
            </li>
          ))}
          <li key="new">
            <button
              className="flex w-full items-center rounded border px-5 py-7 hover:bg-gray-100"
              onClick={() => setNewCashDrawer(NEW_CASH_DRAWER)}
            >
              <PlusIcon className="h-5 w-5 text-primary-500" />
              <div className="ml-3 flex-1 text-left">
                <h4 className="font-bold">{t("cashDrawer.new")}</h4>
              </div>
              <ChevronRightIcon aria-hidden="true" className="h-5 w-5 text-gray-600" />
            </button>
          </li>
        </ul>
        <AlertError>{error}</AlertError>
        <nav className="border-t px-4 py-4 sm:px-6">
          <Button block kind="light" onClick={() => onClose()}>
            {t("generic.cancel")}
          </Button>
        </nav>
      </div>
    </>
  );
}

const CashDrawerSlider = Slider;

export { CashDrawerSlider, OpenCashDrawer, CashDrawerDetail };
